// Groups Component - Full groups functionality UI
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  createGroup,
  getGroup,
  updateGroup,
  deleteGroup,
  joinGroup,
  leaveGroup,
  inviteToGroup,
  acceptGroupInvitation,
  discoverGroups,
  getUserGroups,
  subscribeToUserGroups,
  createGroupPost,
  getGroupPosts,
  subscribeToGroupPosts,
  togglePostLike,
  addPostComment,
  createGroupEvent,
  getGroupEvents,
  rsvpToEvent,
  cancelRsvpToEvent,
  getPendingInvitations,
  addModerator,
  removeModerator
} from '../services/groupsService';
import { getUsernameFromWallet, getUserProfileByWallet } from '../userMappingService';
import { formatAddress } from '../cryptoOperations';

const Groups = ({ walletAddress, onClose, onOpenMemberProfile, isMobile = false }) => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('myGroups'); // 'myGroups', 'discover', 'groupView'
  const [groupViewTab, setGroupViewTab] = useState('posts'); // 'posts', 'events', 'members', 'settings'
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  
  // Discovery state
  const [discoverGroupsList, setDiscoverGroupsList] = useState([]);
  const [discoverTags, setDiscoverTags] = useState('');
  const [discoverPrivacy, setDiscoverPrivacy] = useState(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  
  // Group view state
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [postContent, setPostContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});
  
  // Create group dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGroupData, setNewGroupData] = useState({
    name: '',
    description: '',
    tags: '',
    privacy: 'public',
    rules: ''
  });
  
  // Create event dialog state
  const [showCreateEventDialog, setShowCreateEventDialog] = useState(false);
  const [newEventData, setNewEventData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    location: ''
  });
  
  // Edit group dialog state
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  
  // Invitations
  const [pendingInvitations, setPendingInvitations] = useState([]);
  
  // User profiles cache
  const [userProfiles, setUserProfiles] = useState({});
  
  // Help state
  const [showHelp, setShowHelp] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState(null);
  const notificationTimeoutRef = useRef(null);
  
  const postsEndRef = useRef(null);
  const eventsEndRef = useRef(null);

  // Helper function to show notification
  const showNotification = (message, type = 'success') => {
    // Clear any existing notification timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    setNotification({ message, type });
    
    // Auto-dismiss after 5 seconds
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
      notificationTimeoutRef.current = null;
    }, 5000);
  };

  // Load user's groups
  useEffect(() => {
    if (!walletAddress) return;

    const loadGroups = async () => {
      try {
        setIsLoading(true);
        const userGroups = await getUserGroups(walletAddress);
        setGroups(userGroups);
      } catch (error) {
        console.error('Error loading groups:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGroups();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToUserGroups(walletAddress, (updatedGroups) => {
      setGroups(updatedGroups);
    });

    return () => {
      if (unsubscribe) unsubscribe();
      // Cleanup notification timeout on unmount
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, [walletAddress]);

  // Load pending invitations
  useEffect(() => {
    if (!walletAddress) return;

    const loadInvitations = async () => {
      try {
        const invitations = await getPendingInvitations(walletAddress);
        setPendingInvitations(invitations);
      } catch (error) {
        console.error('Error loading invitations:', error);
      }
    };

    loadInvitations();
  }, [walletAddress]);

  // Load group data when selected
  useEffect(() => {
    if (!selectedGroup || !walletAddress) return;

    const loadGroupData = async () => {
      try {
        const group = await getGroup(selectedGroup.id || selectedGroup.groupId);
        if (group) {
          setSelectedGroup(group);
          setGroupMembers(group.members || []);
        }
      } catch (error) {
        console.error('Error loading group data:', error);
      }
    };

    loadGroupData();
  }, [selectedGroup?.id || selectedGroup?.groupId, walletAddress]);

  // Load posts when viewing a group
  useEffect(() => {
    if (!selectedGroup || groupViewTab !== 'posts') return;

    const loadPosts = async () => {
      try {
        const groupPosts = await getGroupPosts(selectedGroup.id || selectedGroup.groupId);
        console.log('Loaded posts:', groupPosts.length, groupPosts);
        setPosts(groupPosts);
      } catch (error) {
        console.error('Error loading posts:', error);
        // If orderBy fails (missing index), try without it
        try {
          console.log('Retrying posts load without orderBy...');
          // We'll need to handle this differently - for now just set empty
          setPosts([]);
        } catch (retryError) {
          console.error('Retry also failed:', retryError);
        }
      }
    };

    loadPosts();

    // Subscribe to real-time updates
    const groupId = selectedGroup.id || selectedGroup.groupId;
    const unsubscribe = subscribeToGroupPosts(groupId, (updatedPosts) => {
      console.log('Subscription updated posts:', updatedPosts.length, updatedPosts);
      setPosts(updatedPosts);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedGroup?.id || selectedGroup?.groupId, groupViewTab]);

  // Load events when viewing a group
  useEffect(() => {
    if (!selectedGroup || groupViewTab !== 'events') return;

    const loadEvents = async () => {
      try {
        const groupEvents = await getGroupEvents(selectedGroup.id || selectedGroup.groupId);
        setEvents(groupEvents);
      } catch (error) {
        console.error('Error loading events:', error);
      }
    };

    loadEvents();
  }, [selectedGroup?.id || selectedGroup?.groupId, groupViewTab]);

  // Get username helper - loads and caches usernames
  const getUsername = useCallback(async (walletAddr) => {
    if (!walletAddr) return formatAddress(walletAddr);
    const normalizedAddr = walletAddr.toLowerCase();
    
    // Check cache first
    if (userProfiles[normalizedAddr]?.username) {
      return userProfiles[normalizedAddr].username;
    }
    
    try {
      const username = await getUsernameFromWallet(walletAddr);
      if (username) {
        setUserProfiles(prev => ({ ...prev, [normalizedAddr]: { username } }));
        return username;
      }
    } catch (error) {
      console.error('Error getting username:', error);
    }
    
    // Fallback to formatted address
    return formatAddress(walletAddr);
  }, [userProfiles]);

  // State for loading usernames
  const [usernames, setUsernames] = useState({});
  
  // Load usernames for posts when they change
  useEffect(() => {
    const loadPostUsernames = async () => {
      const addresses = new Set();
      
      // Collect all unique addresses from posts and comments
      posts.forEach(post => {
        if (post.author) addresses.add(post.author.toLowerCase());
        if (post.comments) {
          post.comments.forEach(comment => {
            if (comment.author) addresses.add(comment.author.toLowerCase());
          });
        }
      });
      
      // Load usernames for all addresses
      const usernamePromises = Array.from(addresses).map(async (addr) => {
        if (!usernames[addr] && !userProfiles[addr]) {
          try {
            const username = await getUsernameFromWallet(addr);
            return { addr, username: username || formatAddress(addr) };
          } catch (error) {
            return { addr, username: formatAddress(addr) };
          }
        }
        return null;
      });
      
      const results = await Promise.all(usernamePromises);
      const newUsernames = {};
      results.forEach(result => {
        if (result) {
          newUsernames[result.addr] = result.username;
        }
      });
      
      if (Object.keys(newUsernames).length > 0) {
        setUsernames(prev => ({ ...prev, ...newUsernames }));
        setUserProfiles(prev => {
          const updated = { ...prev };
          Object.entries(newUsernames).forEach(([addr, username]) => {
            updated[addr] = { username };
          });
          return updated;
        });
      }
    };
    
    if (posts.length > 0) {
      loadPostUsernames();
    }
  }, [posts]);

  // Load usernames for members
  useEffect(() => {
    const loadMemberUsernames = async () => {
      if (groupMembers.length === 0) return;
      
      const usernamePromises = groupMembers.map(async (memberAddr) => {
        const normalizedAddr = memberAddr.toLowerCase();
        if (!usernames[normalizedAddr] && !userProfiles[normalizedAddr]) {
          try {
            const username = await getUsernameFromWallet(memberAddr);
            return { addr: normalizedAddr, username: username || formatAddress(memberAddr) };
          } catch (error) {
            return { addr: normalizedAddr, username: formatAddress(memberAddr) };
          }
        }
        return null;
      });
      
      const results = await Promise.all(usernamePromises);
      const newUsernames = {};
      results.forEach(result => {
        if (result) {
          newUsernames[result.addr] = result.username;
        }
      });
      
      if (Object.keys(newUsernames).length > 0) {
        setUsernames(prev => ({ ...prev, ...newUsernames }));
        setUserProfiles(prev => {
          const updated = { ...prev };
          Object.entries(newUsernames).forEach(([addr, username]) => {
            updated[addr] = { username };
          });
          return updated;
        });
      }
    };
    
    loadMemberUsernames();
  }, [groupMembers]);

  // Load usernames for events
  useEffect(() => {
    const loadEventUsernames = async () => {
      if (events.length === 0) return;
      
      const addresses = new Set();
      events.forEach(event => {
        if (event.creator) addresses.add(event.creator.toLowerCase());
      });
      
      const usernamePromises = Array.from(addresses).map(async (addr) => {
        if (!usernames[addr] && !userProfiles[addr]) {
          try {
            const username = await getUsernameFromWallet(addr);
            return { addr, username: username || formatAddress(addr) };
          } catch (error) {
            return { addr, username: formatAddress(addr) };
          }
        }
        return null;
      });
      
      const results = await Promise.all(usernamePromises);
      const newUsernames = {};
      results.forEach(result => {
        if (result) {
          newUsernames[result.addr] = result.username;
        }
      });
      
      if (Object.keys(newUsernames).length > 0) {
        setUsernames(prev => ({ ...prev, ...newUsernames }));
        setUserProfiles(prev => {
          const updated = { ...prev };
          Object.entries(newUsernames).forEach(([addr, username]) => {
            updated[addr] = { username };
          });
          return updated;
        });
      }
    };
    
    loadEventUsernames();
  }, [events]);
  
  // Helper to get display name (username or formatted address)
  const getDisplayName = useCallback((walletAddr) => {
    if (!walletAddr) return 'Unknown';
    const normalizedAddr = walletAddr.toLowerCase();
    return userProfiles[normalizedAddr]?.username || usernames[normalizedAddr] || formatAddress(walletAddr);
  }, [userProfiles, usernames]);

  // Create group handler
  const handleCreateGroup = async () => {
    if (!newGroupData.name.trim()) {
      window.alert('Please enter a group name');
      return;
    }

    try {
      setIsCreating(true);
      const tags = newGroupData.tags.split(',').map(t => t.trim()).filter(t => t);
      const rules = newGroupData.rules.split('\n').filter(r => r.trim());

      const groupId = await createGroup(walletAddress, {
        name: newGroupData.name,
        description: newGroupData.description,
        tags,
        privacy: newGroupData.privacy,
        rules
      });

      const newGroup = await getGroup(groupId);
      setGroups(prev => [...prev, newGroup]);
      setShowCreateDialog(false);
      setNewGroupData({
        name: '',
        description: '',
        tags: '',
        privacy: 'public',
        rules: ''
      });
      setSelectedGroup(newGroup);
      setActiveTab('groupView');
    } catch (error) {
      console.error('Error creating group:', error);
      window.alert(`Error creating group: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Discover groups handler
  const handleDiscover = async () => {
    try {
      setIsDiscovering(true);
      const tags = discoverTags.split(',').map(t => t.trim()).filter(t => t);
      const discovered = await discoverGroups(tags, discoverPrivacy, 20);
      setDiscoverGroupsList(discovered);
    } catch (error) {
      console.error('Error discovering groups:', error);
      window.alert(`Error discovering groups: ${error.message}`);
    } finally {
      setIsDiscovering(false);
    }
  };

  // Join group handler
  const handleJoinGroup = async (group) => {
    try {
      await joinGroup(group.id || group.groupId, walletAddress);
      
      // Refresh groups list to show the updated membership
      const userGroups = await getUserGroups(walletAddress);
      setGroups(userGroups);
      
      // Update selected group if it's the one we just joined
      if (selectedGroup && (selectedGroup.id === group.id || selectedGroup.groupId === group.groupId)) {
        const updatedGroup = await getGroup(group.id || group.groupId);
        setSelectedGroup(updatedGroup);
      }
      
      // If we're in discover tab and joined a group, switch to myGroups tab to see it
      if (activeTab === 'discover') {
        setActiveTab('myGroups');
      }
      
      showNotification('Successfully joined group!', 'success');
    } catch (error) {
      console.error('Error joining group:', error);
      showNotification(`Error joining group: ${error.message}`, 'error');
    }
  };

  // Leave group handler
  const handleLeaveGroup = async () => {
    if (!selectedGroup) return;
    if (!window.confirm('Are you sure you want to leave this group?')) return;

    try {
      await leaveGroup(selectedGroup.id || selectedGroup.groupId, walletAddress);
      setGroups(prev => prev.filter(g => (g.id || g.groupId) !== (selectedGroup.id || selectedGroup.groupId)));
      setSelectedGroup(null);
      setActiveTab('myGroups');
      window.alert('Left group successfully');
    } catch (error) {
      console.error('Error leaving group:', error);
      window.alert(`Error leaving group: ${error.message}`);
    }
  };

  // Edit group handler
  const handleRenameGroup = async () => {
    if (!selectedGroup || !newGroupName.trim()) return;

    try {
      setIsRenaming(true);
      const groupId = selectedGroup.id || selectedGroup.groupId;
      await updateGroup(groupId, walletAddress, { 
        name: newGroupName.trim(),
        description: newGroupDescription.trim()
      });
      
      // Update the selected group and groups list
      const updatedGroup = await getGroup(groupId);
      setSelectedGroup(updatedGroup);
      setGroups(prev => prev.map(g => 
        (g.id || g.groupId) === groupId ? updatedGroup : g
      ));
      
      setShowRenameDialog(false);
      setNewGroupName('');
      setNewGroupDescription('');
      showNotification('Group updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating group:', error);
      showNotification(`Error updating group: ${error.message}`, 'error');
    } finally {
      setIsRenaming(false);
    }
  };

  // Delete group handler
  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    if (!window.confirm('Are you sure you want to delete this group? This cannot be undone.')) return;

    try {
      await deleteGroup(selectedGroup.id || selectedGroup.groupId, walletAddress);
      setGroups(prev => prev.filter(g => (g.id || g.groupId) !== (selectedGroup.id || selectedGroup.groupId)));
      setSelectedGroup(null);
      setActiveTab('myGroups');
      window.alert('Group deleted successfully');
    } catch (error) {
      console.error('Error deleting group:', error);
      window.alert(`Error deleting group: ${error.message}`);
    }
  };

  // Create post handler
  const handleCreatePost = async () => {
    if (!postContent.trim() || !selectedGroup) return;

    const postContentText = postContent.trim();
    const groupId = selectedGroup.id || selectedGroup.groupId;
    
    try {
      setIsPosting(true);
      setPostContent(''); // Clear input immediately for better UX
      
      console.log('Creating post in group:', groupId);
      const postId = await createGroupPost(
        groupId,
        walletAddress,
        { content: postContentText }
      );
      console.log('Post created with ID:', postId);
      
      showNotification('Post created successfully!', 'success');
      
      // Reload posts after a short delay to allow Firestore to process
      // The subscription should also update automatically
      setTimeout(async () => {
        try {
          console.log('Reloading posts after creation...');
          const groupPosts = await getGroupPosts(groupId, 50);
          console.log('Reloaded posts:', groupPosts.length);
          setPosts(groupPosts);
          
          // Scroll to bottom to see new post
          setTimeout(() => {
            if (postsEndRef.current) {
              postsEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        } catch (err) {
          console.error('Error reloading posts:', err);
        }
      }, 800);
      
      // Additional reload after longer delay as backup
      setTimeout(async () => {
        try {
          const groupPosts = await getGroupPosts(groupId, 50);
          console.log('Backup reload got', groupPosts.length, 'posts');
          setPosts(groupPosts);
        } catch (err) {
          console.error('Error in backup reload:', err);
        }
      }, 2500);
    } catch (error) {
      console.error('Error creating post:', error);
      showNotification(`Error creating post: ${error.message}`, 'error');
      // Restore post content if there was an error
      setPostContent(postContentText);
    } finally {
      setIsPosting(false);
    }
  };

  // Toggle like handler
  const handleToggleLike = async (post) => {
    try {
      await togglePostLike(post.id || post.postId, walletAddress);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // Add comment handler
  const handleAddComment = async (post, commentText) => {
    if (!commentText.trim()) return;

    try {
      await addPostComment(post.id || post.postId, walletAddress, commentText);
      setCommentInputs(prev => ({ ...prev, [post.id || post.postId]: '' }));
    } catch (error) {
      console.error('Error adding comment:', error);
      window.alert(`Error adding comment: ${error.message}`);
    }
  };

  // Create event handler
  const handleCreateEvent = async () => {
    if (!newEventData.title.trim() || !selectedGroup) return;

    try {
      await createGroupEvent(
        selectedGroup.id || selectedGroup.groupId,
        walletAddress,
        {
          title: newEventData.title,
          description: newEventData.description,
          startDate: newEventData.startDate,
          endDate: newEventData.endDate,
          location: newEventData.location
        }
      );
      setShowCreateEventDialog(false);
      setNewEventData({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        location: ''
      });
      
      // Reload events
      const groupEvents = await getGroupEvents(selectedGroup.id || selectedGroup.groupId);
      setEvents(groupEvents);
    } catch (error) {
      console.error('Error creating event:', error);
      window.alert(`Error creating event: ${error.message}`);
    }
  };

  // RSVP handler
  const handleRsvp = async (event, isRsvped) => {
    try {
      if (isRsvped) {
        await cancelRsvpToEvent(event.id || event.eventId, walletAddress);
      } else {
        await rsvpToEvent(event.id || event.eventId, walletAddress);
      }
      
      // Reload events
      const groupEvents = await getGroupEvents(selectedGroup.id || selectedGroup.groupId);
      setEvents(groupEvents);
    } catch (error) {
      console.error('Error updating RSVP:', error);
      window.alert(`Error updating RSVP: ${error.message}`);
    }
  };

  // Accept invitation handler
  const handleAcceptInvitation = async (invitation) => {
    try {
      await acceptGroupInvitation(invitation.id, walletAddress);
      const invitations = await getPendingInvitations(walletAddress);
      setPendingInvitations(invitations);
      
      // Reload groups
      const userGroups = await getUserGroups(walletAddress);
      setGroups(userGroups);
      
      window.alert('Invitation accepted!');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      window.alert(`Error accepting invitation: ${error.message}`);
    }
  };

  // Check if user is member
  const isMember = (group) => {
    if (!group || !walletAddress) return false;
    return (group.members || []).includes(walletAddress.toLowerCase());
  };

  // Check if user is creator
  const isCreator = (group) => {
    if (!group || !walletAddress) return false;
    return (group.creator || '').toLowerCase() === walletAddress.toLowerCase();
  };

  // Check if user is moderator
  const isModerator = (group) => {
    if (!group || !walletAddress) return false;
    return (group.moderators || []).includes(walletAddress.toLowerCase());
  };

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Format relative date helper
  const formatRelativeDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(timestamp);
  };

  const containerStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    left: isMobile ? 0 : 'auto',
    width: isMobile ? '100%' : '350px',
    height: '100vh',
    backgroundColor: '#fff',
    borderLeft: isMobile ? 'none' : '1px solid #e0e0e0',
    overflowY: 'auto',
    zIndex: 1005,
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <div id="groups-section" style={containerStyle} className={isMobile ? 'mobile-panel' : ''}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f9f9f9',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Groups</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setShowHelp(!showHelp)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '1px solid #ccc',
                backgroundColor: showHelp ? '#f0f0f0' : '#fff',
                color: '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.borderColor = '#999';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = showHelp ? '#f0f0f0' : '#fff';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              title="How Groups Work"
            >
              ?
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0',
                color: '#666'
              }}
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Help Section */}
        {showHelp && (
          <div style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            fontSize: '13px',
            lineHeight: '1.6',
            color: '#333'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
              How Groups Work
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ marginTop: 0, marginBottom: '8px' }}>
                <strong>Groups</strong> allow you to connect with people who share similar interests and organize around common topics.
              </p>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '6px' }}>Create or join groups based on your interests and tags</li>
                <li style={{ marginBottom: '6px' }}>Post updates, share content, and start discussions</li>
                <li style={{ marginBottom: '6px' }}>Organize and attend group events</li>
                <li style={{ marginBottom: '6px' }}>Discover new groups through tag-based search</li>
                <li style={{ marginBottom: '6px' }}>Manage group members and privacy settings</li>
              </ul>
            </div>
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#fff3cd',
              borderRadius: '6px',
              border: '1px solid #ffc107'
            }}>
              <p style={{ margin: 0, fontWeight: '600', color: '#856404' }}>
                ⚠️ Encryption Notice
              </p>
              <p style={{ margin: '6px 0 0 0', color: '#856404' }}>
                <strong>Groups are NOT encrypted.</strong> All group content including posts, comments, events, and member information is stored unencrypted in Firestore. Only group creators and moderators can manage group settings and members.
              </p>
            </div>
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
              <p style={{ margin: '0 0 6px 0' }}><strong>Privacy Levels:</strong></p>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Public:</strong> Anyone can join</li>
                <li><strong>Private:</strong> Requires invitation</li>
                <li><strong>Invite Only:</strong> Members must be invited</li>
              </ul>
            </div>
          </div>
        )}
        
        {/* Tabs */}
        {!selectedGroup && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setActiveTab('myGroups')}
              style={{
                flex: 1,
                padding: '8px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: activeTab === 'myGroups' ? '#4a90e2' : '#f0f0f0',
                color: activeTab === 'myGroups' ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              My Groups
            </button>
            <button
              onClick={() => setActiveTab('discover')}
              style={{
                flex: 1,
                padding: '8px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: activeTab === 'discover' ? '#4a90e2' : '#f0f0f0',
                color: activeTab === 'discover' ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Discover
            </button>
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div style={{ padding: '12px 20px', backgroundColor: '#fff3cd', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#856404' }}>
            Pending Invitations ({pendingInvitations.length})
          </div>
          {pendingInvitations.map(inv => (
            <div key={inv.id} style={{ marginBottom: '8px', fontSize: '13px' }}>
              <div style={{ marginBottom: '4px' }}>Invitation to join a group</div>
              <button
                onClick={() => handleAcceptInvitation(inv)}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Accept
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading groups...
          </div>
        ) : activeTab === 'myGroups' && !selectedGroup ? (
          <div style={{ padding: '20px' }}>
            <button
              onClick={() => setShowCreateDialog(true)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#4a90e2',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '20px'
              }}
            >
              + Create Group
            </button>
            
            {groups.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                <p>You're not a member of any groups yet.</p>
                <p style={{ fontSize: '13px', marginTop: '8px' }}>Create one or discover groups!</p>
              </div>
            ) : (
              <div>
                {groups.map(group => (
                  <div
                    key={group.id || group.groupId}
                    onClick={() => {
                      setSelectedGroup(group);
                      setActiveTab('groupView');
                      setGroupViewTab('posts');
                    }}
                    style={{
                      padding: '16px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9f9f9';
                      e.currentTarget.style.borderColor = '#4a90e2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                      {group.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      {group.description || 'No description'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {group.tags && group.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '2px 8px',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '12px',
                            fontSize: '11px'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {group.memberCount || 0} members • {group.privacy}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'discover' && !selectedGroup ? (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Search by tags (comma-separated)"
                value={discoverTags}
                onChange={(e) => setDiscoverTags(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}
              />
              <select
                value={discoverPrivacy || ''}
                onChange={(e) => setDiscoverPrivacy(e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '12px'
                }}
              >
                <option value="">All Privacy Levels</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="invite-only">Invite Only</option>
              </select>
              <button
                onClick={handleDiscover}
                disabled={isDiscovering}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: isDiscovering ? '#ccc' : '#4a90e2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isDiscovering ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {isDiscovering ? 'Searching...' : 'Search Groups'}
              </button>
            </div>

            {discoverGroupsList.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                <p>No groups found.</p>
                <p style={{ fontSize: '13px', marginTop: '8px' }}>Try different search terms or create your own group!</p>
              </div>
            ) : (
              <div>
                {discoverGroupsList.map(group => (
                  <div
                    key={group.id || group.groupId}
                    style={{
                      padding: '16px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                      {group.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      {group.description || 'No description'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {group.tags && group.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '2px 8px',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '12px',
                            fontSize: '11px'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '12px' }}>
                      {group.memberCount || 0} members • {group.privacy}
                    </div>
                    {isMember(group) ? (
                      <button
                        onClick={() => {
                          setSelectedGroup(group);
                          setActiveTab('groupView');
                          setGroupViewTab('posts');
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: '#4a90e2',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Open Group
                      </button>
                    ) : (
                      <button
                        onClick={() => handleJoinGroup(group)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Join Group
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : selectedGroup && activeTab === 'groupView' ? (
          <div>
            {/* Group Header */}
            <div style={{ padding: '20px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f9f9f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                    {selectedGroup.name}
                  </h3>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    {selectedGroup.description || 'No description'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {selectedGroup.memberCount || 0} members • {selectedGroup.privacy}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedGroup(null);
                    setActiveTab('myGroups');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '0',
                    color: '#666'
                  }}
                >
                  ← Back
                </button>
              </div>
              
              {/* Group View Tabs */}
              <div style={{ display: 'flex', gap: '4px', marginTop: '12px' }}>
                {['posts', 'events', 'members', 'settings'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setGroupViewTab(tab)}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: groupViewTab === tab ? '#4a90e2' : '#f0f0f0',
                      color: groupViewTab === tab ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Posts Tab */}
            {groupViewTab === 'posts' && (
              <div style={{ padding: '20px' }}>
                {/* Create Post */}
                <div style={{ marginBottom: '20px' }}>
                  <textarea
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    placeholder="What's on your mind?"
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      marginBottom: '8px'
                    }}
                  />
                  <button
                    onClick={handleCreatePost}
                    disabled={!postContent.trim() || isPosting}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: (!postContent.trim() || isPosting) ? '#ccc' : '#4a90e2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (!postContent.trim() || isPosting) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {isPosting ? 'Posting...' : 'Post'}
                  </button>
                </div>

                {/* Posts List */}
                {posts.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                    <p>No posts yet.</p>
                    <p style={{ fontSize: '13px', marginTop: '8px' }}>Be the first to post!</p>
                  </div>
                ) : (
                  <div>
                    {posts.map(post => (
                      <div
                        key={post.id || post.postId}
                        style={{
                          padding: '16px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          marginBottom: '16px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onOpenMemberProfile && post.author) {
                                onOpenMemberProfile(post.author);
                              }
                            }}
                            style={{ 
                              fontSize: '13px', 
                              fontWeight: '600',
                              cursor: onOpenMemberProfile && post.author ? 'pointer' : 'default',
                              color: onOpenMemberProfile && post.author ? '#4a90e2' : 'inherit',
                              textDecoration: onOpenMemberProfile && post.author ? 'none' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (onOpenMemberProfile && post.author) {
                                e.currentTarget.style.textDecoration = 'underline';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (onOpenMemberProfile && post.author) {
                                e.currentTarget.style.textDecoration = 'none';
                              }
                            }}
                          >
                            {getDisplayName(post.author)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            {formatRelativeDate(post.createdAt)}
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '12px', whiteSpace: 'pre-wrap' }}>
                          {post.content}
                        </div>
                        
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                          <button
                            onClick={() => handleToggleLike(post)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '13px',
                              color: (post.likes || []).includes(walletAddress.toLowerCase()) ? '#e74c3c' : '#666'
                            }}
                          >
                            ♥ {post.likesCount || 0}
                          </button>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            💬 {post.commentsCount || 0} comments
                          </div>
                        </div>

                        {/* Comments */}
                        {post.comments && post.comments.length > 0 && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                            {post.comments.map((comment, idx) => (
                              <div key={idx} style={{ marginBottom: '8px', fontSize: '13px' }}>
                                <div 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onOpenMemberProfile && comment.author) {
                                      onOpenMemberProfile(comment.author);
                                    }
                                  }}
                                  style={{ 
                                    fontWeight: '600', 
                                    marginBottom: '2px',
                                    cursor: onOpenMemberProfile && comment.author ? 'pointer' : 'default',
                                    color: onOpenMemberProfile && comment.author ? '#4a90e2' : 'inherit',
                                    textDecoration: onOpenMemberProfile && comment.author ? 'none' : 'none'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (onOpenMemberProfile && comment.author) {
                                      e.currentTarget.style.textDecoration = 'underline';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (onOpenMemberProfile && comment.author) {
                                      e.currentTarget.style.textDecoration = 'none';
                                    }
                                  }}
                                >
                                  {getDisplayName(comment.author)}
                                </div>
                                <div style={{ color: '#666' }}>{comment.content}</div>
                                <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                  {formatRelativeDate(comment.createdAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Comment Input */}
                        <div style={{ marginTop: '12px' }}>
                          <input
                            type="text"
                            placeholder="Add a comment..."
                            value={commentInputs[post.id || post.postId] || ''}
                            onChange={(e) => setCommentInputs(prev => ({
                              ...prev,
                              [post.id || post.postId]: e.target.value
                            }))}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddComment(post, commentInputs[post.id || post.postId] || '');
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ccc',
                              borderRadius: '6px',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    <div ref={postsEndRef} />
                  </div>
                )}
              </div>
            )}

            {/* Events Tab */}
            {groupViewTab === 'events' && (
              <div style={{ padding: '20px' }}>
                {(isMember(selectedGroup) || isModerator(selectedGroup)) && (
                  <button
                    onClick={() => setShowCreateEventDialog(true)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#4a90e2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '20px'
                    }}
                  >
                    + Create Event
                  </button>
                )}

                {events.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                    <p>No events scheduled.</p>
                    <p style={{ fontSize: '13px', marginTop: '8px' }}>Create an event to bring members together!</p>
                  </div>
                ) : (
                  <div>
                    {events.map(event => {
                      const isRsvped = (event.attendees || []).includes(walletAddress.toLowerCase());
                      return (
                        <div
                          key={event.id || event.eventId}
                          style={{
                            padding: '16px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: '16px'
                          }}
                        >
                          <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                            {event.title}
                          </div>
                          {event.description && (
                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                              {event.description}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                            📅 {event.startDate ? formatDate(event.startDate) : 'TBD'}
                            {event.endDate && ` - ${formatDate(event.endDate)}`}
                          </div>
                          {event.location && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                              📍 {event.location}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#999', marginBottom: '12px' }}>
                            {event.attendeesCount || 0} attendees
                          </div>
                          {isMember(selectedGroup) && (
                            <button
                              onClick={() => handleRsvp(event, isRsvped)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                backgroundColor: isRsvped ? '#dc3545' : '#28a745',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              {isRsvped ? 'Cancel RSVP' : 'RSVP'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div ref={eventsEndRef} />
                  </div>
                )}
              </div>
            )}

            {/* Members Tab */}
            {groupViewTab === 'members' && (
              <div style={{ padding: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                  Members ({selectedGroup.memberCount || 0})
                </div>
                {groupMembers.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                    No members found.
                  </div>
                ) : (
                  <div>
                    {groupMembers.map(member => {
                      const isMemCreator = member.toLowerCase() === (selectedGroup.creator || '').toLowerCase();
                      const isMemModerator = (selectedGroup.moderators || []).includes(member.toLowerCase());
                      
                      return (
                        <div
                          key={member}
                          onClick={() => {
                            if (onOpenMemberProfile) {
                              onOpenMemberProfile(member);
                            }
                          }}
                          style={{
                            padding: '12px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: onOpenMemberProfile ? 'pointer' : 'default',
                            transition: 'all 0.2s',
                            backgroundColor: '#fff'
                          }}
                          onMouseEnter={(e) => {
                            if (onOpenMemberProfile) {
                              e.currentTarget.style.backgroundColor = '#f9f9f9';
                              e.currentTarget.style.borderColor = '#4a90e2';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (onOpenMemberProfile) {
                              e.currentTarget.style.backgroundColor = '#fff';
                              e.currentTarget.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          <div>
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onOpenMemberProfile && member) {
                                  onOpenMemberProfile(member);
                                }
                              }}
                              style={{ 
                                fontSize: '13px', 
                                fontWeight: '600',
                                cursor: onOpenMemberProfile && member ? 'pointer' : 'default',
                                color: onOpenMemberProfile && member ? '#4a90e2' : 'inherit',
                                textDecoration: onOpenMemberProfile && member ? 'none' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                if (onOpenMemberProfile && member) {
                                  e.currentTarget.style.textDecoration = 'underline';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (onOpenMemberProfile && member) {
                                  e.currentTarget.style.textDecoration = 'none';
                                }
                              }}
                            >
                              {getDisplayName(member)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#999' }}>
                              {isMemCreator ? 'Creator' : isMemModerator ? 'Moderator' : 'Member'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {groupViewTab === 'settings' && (
              <div style={{ padding: '20px' }}>
                {(isCreator(selectedGroup) || isModerator(selectedGroup)) ? (
                  <div>
                    <button
                      onClick={() => {
                        setNewGroupName(selectedGroup.name || '');
                        setNewGroupDescription(selectedGroup.description || '');
                        setShowRenameDialog(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#4a90e2',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '12px'
                      }}
                    >
                      Edit Group
                    </button>
                    {isCreator(selectedGroup) && (
                      <>
                        <button
                          onClick={handleDeleteGroup}
                          style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '12px'
                          }}
                        >
                          Delete Group
                        </button>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Only the creator can delete the group. This action cannot be undone.
                        </div>
                      </>
                    )}
                  </div>
                ) : isMember(selectedGroup) ? (
                  <div>
                    <button
                      onClick={handleLeaveGroup}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Leave Group
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
                    You are not a member of this group.
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Create Group Dialog */}
      {showCreateDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0 }}>Create Group</h3>
            <input
              type="text"
              placeholder="Group Name *"
              value={newGroupData.name}
              onChange={(e) => setNewGroupData(prev => ({ ...prev, name: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            <textarea
              placeholder="Description"
              value={newGroupData.description}
              onChange={(e) => setNewGroupData(prev => ({ ...prev, description: e.target.value }))}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px',
                resize: 'vertical'
              }}
            />
            <input
              type="text"
              placeholder="Tags (comma-separated)"
              value={newGroupData.tags}
              onChange={(e) => setNewGroupData(prev => ({ ...prev, tags: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            <select
              value={newGroupData.privacy}
              onChange={(e) => setNewGroupData(prev => ({ ...prev, privacy: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="invite-only">Invite Only</option>
            </select>
            <textarea
              placeholder="Group Rules (one per line)"
              value={newGroupData.rules}
              onChange={(e) => setNewGroupData(prev => ({ ...prev, rules: e.target.value }))}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewGroupData({
                    name: '',
                    description: '',
                    tags: '',
                    privacy: 'public',
                    rules: ''
                  });
                }}
                disabled={isCreating}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  cursor: isCreating ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={!newGroupData.name.trim() || isCreating}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: (!newGroupData.name.trim() || isCreating) ? '#ccc' : '#4a90e2',
                  color: '#fff',
                  cursor: (!newGroupData.name.trim() || isCreating) ? 'not-allowed' : 'pointer'
                }}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Dialog */}
      {showRenameDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0 }}>Edit Group</h3>
            <input
              type="text"
              placeholder="Group Name *"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
              autoFocus
            />
            <textarea
              placeholder="Description"
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRenameDialog(false);
                  setNewGroupName('');
                  setNewGroupDescription('');
                }}
                disabled={isRenaming}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  cursor: isRenaming ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameGroup}
                disabled={!newGroupName.trim() || isRenaming}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: (!newGroupName.trim() || isRenaming) ? '#ccc' : '#4a90e2',
                  color: '#fff',
                  cursor: (!newGroupName.trim() || isRenaming) ? 'not-allowed' : 'pointer'
                }}
              >
                {isRenaming ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Dialog */}
      {showCreateEventDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>Create Event</h3>
            <input
              type="text"
              placeholder="Event Title *"
              value={newEventData.title}
              onChange={(e) => setNewEventData(prev => ({ ...prev, title: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            <textarea
              placeholder="Description"
              value={newEventData.description}
              onChange={(e) => setNewEventData(prev => ({ ...prev, description: e.target.value }))}
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px',
                resize: 'vertical'
              }}
            />
            <input
              type="datetime-local"
              placeholder="Start Date"
              value={newEventData.startDate}
              onChange={(e) => setNewEventData(prev => ({ ...prev, startDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            <input
              type="datetime-local"
              placeholder="End Date"
              value={newEventData.endDate}
              onChange={(e) => setNewEventData(prev => ({ ...prev, endDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            <input
              type="text"
              placeholder="Location"
              value={newEventData.location}
              onChange={(e) => setNewEventData(prev => ({ ...prev, location: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateEventDialog(false);
                  setNewEventData({
                    title: '',
                    description: '',
                    startDate: '',
                    endDate: '',
                    location: ''
                  });
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEvent}
                disabled={!newEventData.title.trim()}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: !newEventData.title.trim() ? '#ccc' : '#4a90e2',
                  color: '#fff',
                  cursor: !newEventData.title.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                Create Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {notification && createPortal(
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: notification.type === 'error' ? '#e74c3c' : '#007bff',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10002,
            fontSize: '14px',
            fontWeight: 500,
            animation: 'slideUp 0.3s ease-out',
            maxWidth: '90%',
            textAlign: 'center'
          }}
        >
          {notification.message}
        </div>,
        document.body
      )}

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Groups;

